<template>
  <Screen>
    <AppBar
      title="Support tickets"
      description="View all support tickets and respond to them"
      :showBackButton="false"
    />
    <Breadcrumbs title="Support tickets" />
    <div style="overflow: hidden">
      <transition name="slide-up">
        <div
          class="alert alert-solid-warning mb-3 alert-dismissible"
          v-if="showAlert"
        >
          <h4 class="mb-1">
            <small
              class="fa fa-exclamation-triangle"
              style="position: relative; top: -1px"
            ></small>
            <b>Beware</b>
          </h4>
          <p class="mb-2 font-weight-bold">
            Do not open links you do not trust. Links can be malicious and you
            might get hacked.
          </p>
          <button
            type="button"
            @click.prevent="dismissAlert"
            class="btn btn-sm btn-dark px-3"
          >
            OK, I understand
          </button>
        </div>
      </transition>
    </div>
    <div class="card">
      <div class="card-body">
        <div class="table-meta-wrapper">
          <SearchBar
            class="search-bar"
            @onSubmit="searchSubmit"
            @onClear="searchCleared"
            v-model="searchInput"
            placeholder="Type and hit enter to search"
          />
          <div class="filters">
            <Dropdown
              header="Select view mode"
              :items="displayModeFilters"
              @selected="displayModeFilterChanged"
            />
          </div>
        </div>
        <div class="table-responsive mt-4" v-if="data.data.length > 0">
          <table class="table mb-0">
            <thead>
              <tr>
                <th style="width: 0">#</th>
                <th>User</th>
                <th>Message</th>
                <th v-if="displayingData != 'open'">Response</th>
                <th v-if="displayingData != 'open'">Resolved by</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, i) in data.data" :key="item.id">
                <td>
                  {{
                    i + 1 + (data.meta.current_page - 1) * data.meta.per_page
                  }}
                </td>
                <th>
                  <span class="d-block">
                    {{ item.user.name }}
                    <inertia-link
                      :href="
                        $route('admin.users.details', {
                          id: item.user.id,
                        })
                      "
                      class="btn btn-rounded-sm btn-primary-neutral ml-1"
                      v-tooltip="'View user'"
                    >
                      <i class="fa fa-eye"></i>
                    </inertia-link>
                  </span>
                  <a
                    class="sub-title"
                    :href="`tel:${item.user.mobile}`"
                    v-text="item.user.mobile"
                    v-tooltip="'Click to call'"
                  ></a>
                  <small>&bull;</small>
                  <a
                    class="sub-title"
                    :href="`mailto:${item.user.email}`"
                    v-text="item.user.email"
                    v-tooltip="'Click to email'"
                  ></a>
                </th>
                <td>
                  <span class="badge badge-warning badge-pill" v-if="!item.seen"
                    >new</span
                  >
                  <span
                    class="badge badge-danger badge-pill"
                    v-if="!item.resolved"
                    >open</span
                  >
                  <div style="max-width: 480px">
                    <VueReadMoreSmooth :lines="2">
                      <span>{{ item.message }}</span>
                    </VueReadMoreSmooth>
                  </div>
                </td>
                <td v-if="displayingData != 'open'">
                  <div style="max-width: 480px" v-if="item.response">
                    <VueReadMoreSmooth :lines="2">
                      <span>{{ item.response }}</span>
                    </VueReadMoreSmooth>
                  </div>
                  <span v-else class="sub-title">-</span>
                </td>
                <th v-if="displayingData != 'open'">
                  <template
                    v-if="item.responded_by && getObjLength(item.responded_by)"
                  >
                    <span class="d-block">
                      {{ item.responded_by.name }}
                      <span
                        v-if="user.id === item.responded_by.id"
                        class="badge badge-success badge-pill"
                        >me</span
                      >
                      <inertia-link
                        v-else
                        :href="
                          $route('admin.users.details', {
                            id: item.responded_by.id,
                          })
                        "
                        class="btn btn-rounded-sm btn-primary-neutral ml-1"
                        v-tooltip="'View user'"
                      >
                        <i class="fa fa-eye"></i>
                      </inertia-link>
                    </span>
                    <a
                      class="sub-title"
                      :href="`tel:${item.responded_by.mobile}`"
                      v-text="item.responded_by.mobile"
                      v-tooltip="'Click to call'"
                    ></a>
                    <small>&bull;</small>
                    <a
                      class="sub-title"
                      :href="`mailto:${item.responded_by.email}`"
                      v-text="item.responded_by.email"
                      v-tooltip="'Click to email'"
                    ></a>
                  </template>
                  <span v-else class="sub-title">-</span>
                </th>
                <td>
                  <span style="color: var(--dark)">
                    {{ item.created_at_human }}
                  </span>
                  <span class="sub-title d-block">
                    {{ item.time_ago }}
                  </span>
                </td>
                <td>
                  <inertia-link
                    class="btn btn-sm"
                    :class="[item.resolved ? 'btn-light' : 'btn-primary']"
                    :href="
                      $route('admin.support.view', {
                        id: item.id,
                      })
                    "
                  >
                    {{ item.resolved ? "View" : "Resolve" }}
                  </inertia-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <NoData title="No tickets added yet" :button="false" v-else />
        <Pagination :rawData="data.meta" :onClick="pageChange" />
      </div>
    </div>
  </Screen>
</template>
<script>
import VueReadMoreSmooth from "vue-read-more-smooth";
import Dropdown from "@/components/dropdown";

const alertLsKey = "support-beware-alert";

export default {
  components: {
    VueReadMoreSmooth,
    Dropdown,
  },
  props: {
    data: {
      type: [Array, Object],
      default: () => [],
    },
    displayingData: {
      type: String,
      default: "default",
    },
    search: {
      type: String,
      default: "",
    },
  },
  watch: {
    search: {
      immediate: true,
      handler(value) {
        this.searchInput = value;
      },
    },
  },
  data() {
    return {
      searchInput: "",
      showAlert: false,
    };
  },
  mounted() {
    this.showBewareAlert();
  },
  methods: {
    searchSubmit(search) {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          search: search,
          page: 1,
        })
      );
    },
    searchCleared() {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          search: "",
          page: 1,
        })
      );
    },
    pageChange(page) {
      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          page: page,
        })
      );
    },
    displayModeFilterChanged(item) {
      if (item.id === this.displayingData) return;

      this.$root.$emit("hold-transition", null);
      this.$inertia.visit(
        this.$route(this.$route().current(), {
          ...this.$route().params,
          display: item.id,
          page: 1,
        })
      );
    },
    replayLink(item) {
      const lineBreak = "%0D%0A";
      const space = "%20";

      let message = `Hello ${item.name},${lineBreak + lineBreak}`;

      message += `In response to your enquiry: '${item.message}'${lineBreak}`;

      message += `We would like to inform you that...${lineBreak + lineBreak}`;

      message += `Kind regards,${lineBreak}The ${this.$page.props.config.app_name} team`;

      return `mailto:${item.email}?subject=${item.subject}&body=${message}`;
    },
    showBewareAlert() {
      const alertDismissed = localStorage.getItem(alertLsKey) || null;

      if (!alertDismissed || alertDismissed == "") {
        this.showAlert = true;
        return;
      }

      const alertDismissedDate = new Date(alertDismissed);

      // check if more than 10 days ago
      if (
        alertDismissedDate.getTime() <
        new Date().getTime() - 10 * 24 * 60 * 60 * 1000
      ) {
        this.showAlert = true;
      }
    },
    dismissAlert(event) {
      // set height as current height for animation
      event.target.parentElement.style.height =
        event.target.parentElement.offsetHeight + "px";

      // set dismissed date in local storage
      localStorage.setItem(alertLsKey, new Date());

      setTimeout(() => {
        this.showAlert = false;
      }, 100);
    },
  },
  computed: {
    displayModeFilters() {
      return [
        {
          id: "all",
          title: '<small class="fa fa-minus-circle mr-2"></small>Display all',
          active: this.displayingData === "all",
        },
        {
          id: "resolved",
          title:
            '<small class="fa fa-check-circle text-success mr-2"></small>Resolved',
          active: this.displayingData === "resolved",
        },
        {
          id: "open",
          title:
            '<small class="fa fa-exclamation-circle text-warning mr-2"></small>Open',
          active: this.displayingData === "open",
        },
      ];
    },
    user() {
      return this.$page.props.user;
    },
  },
};
</script>
<style lang="scss" scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.5s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  position: relative;
  opacity: 0;
  height: 0px;
  z-index: 0 !important;
}

.slide-up-active {
  transition: all 0.5s ease;
  overflow: hidden;
}

.slide-up-enter,
.slide-up-leave-active {
  opacity: 0;
  transform: none;
  height: 0 !important;
  padding: 0 !important;
  overflow: hidden;
  overflow: hidden;
}
</style>